'use client';

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { clsx } from 'clsx';
import { useRouter } from 'next/navigation';

import { useLocale, usePathname } from '@monoimin/intl';
import { Icons } from '@monoimin/atoms';
import { Locales } from '@monoimin/sb-types';
import { langTitleDict } from '@monoimin/constants';

import type { HeaderCompactProps } from '../HeaderCompact';

type LocaleSwitcherProps = {
  localesList: NonNullable<HeaderCompactProps['localesList']>;
  className?: string;
  optionsClassName?: string;
  withActiveLocaleText?: boolean;
};

export const LocaleSwitcher = ({
  localesList,
  className,
  optionsClassName,
  withActiveLocaleText,
}: LocaleSwitcherProps) => {
  const pathname = usePathname();

  const locale = useLocale();

  const router = useRouter();

  const onLocaleChange = (locale: Locales) => {
    router.replace(`/${locale}${pathname}`);
  };

  return (
    <Listbox
      as='div'
      value={locale}
      onChange={onLocaleChange}
      className={clsx('listbox', className)}
    >
      <ListboxButton className='group flex items-center gap-0.5'>
        <Icons className='h-5 w-5 group-hover:opacity-70' icon='lang' />
        {withActiveLocaleText && <span>{langTitleDict[locale]}</span>}
        <Icons
          className='h-5 w-5 duration-200 group-hover:opacity-70 group-aria-expanded:rotate-180'
          icon='dropdown'
        />
      </ListboxButton>
      <ListboxOptions
        as='ul'
        className={clsx('listbox__options', optionsClassName)}
      >
        {localesList.map((item) => {
          return (
            <ListboxOption
              as='li'
              value={item.locale}
              key={item.locale}
              className='listbox__option'
            >
              {item.title}
            </ListboxOption>
          );
        })}
      </ListboxOptions>
    </Listbox>
  );
};
